'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts';

interface FundAllocation {
  category: string;
  amount: number;
  percentage: number;
  color: string;
  description: string;
}

interface QuarterlyData {
  quarter: string;
  raised: number;
  deployed: number;
}

const TransparencySection = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const fundAllocation: FundAllocation[] = [
    { category: 'Direct Aid', amount: 6545000, percentage: 35, color: '#8B4513', description: 'Emergency relief and humanitarian assistance' },
    { category: 'Education', amount: 5610000, percentage: 30, color: '#D2691E', description: 'Peace education and forgiveness training' },
    { category: 'Community Programs', amount: 3366000, percentage: 18, color: '#FFD700', description: 'Local chapter support and events' },
    { category: 'Research', amount: 1496000, percentage: 8, color: '#228B22', description: 'Impact studies and evidence building' },
    { category: 'Operations', amount: 935000, percentage: 5, color: '#4a90a4', description: 'Administrative and platform costs' },
    { category: 'Founder Recognition', amount: 748000, percentage: 4, color: '#9370DB', description: 'Supports initiative, leadership and full-time dedication to the movement' }
  ];

  const quarterlyData: QuarterlyData[] = [
    { quarter: 'Q1 2025', raised: 3800000, deployed: 3420000 },
    { quarter: 'Q2 2025', raised: 4200000, deployed: 3990000 },
    { quarter: 'Q3 2025', raised: 5100000, deployed: 4845000 },
    { quarter: 'Q4 2025', raised: 5600000, deployed: 5395000 }
  ];

  const certifications = [
    { name: 'GuideStar Platinum', icon: 'StarIcon' },
    { name: 'BBB Accredited', icon: 'ShieldCheckIcon' },
    { name: 'Charity Navigator 4-Star', icon: 'TrophyIcon' },
    { name: 'PCI-DSS Compliant', icon: 'LockClosedIcon' }
  ];

  if (!isHydrated) {
    return (
      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
              Financial Transparency
            </h2>
            <p className="text-lg text-muted-foreground font-body">Loading transparency data...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            Financial Transparency & Accountability
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            We believe in complete transparency. Every dollar is tracked, verified, and reported with independent audits ensuring your trust is well-placed.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
            <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
              <Icon name="ChartPieIcon" size={28} className="text-primary" variant="solid" />
              <span>Fund Allocation Breakdown</span>
            </h3>

            <div className="h-80 mb-6" aria-label="Fund Allocation Pie Chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={fundAllocation}
                    dataKey="percentage"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => `${entry.category}: ${entry.percentage}%`}
                  >
                    {fundAllocation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-4">
              {fundAllocation.map((item) => (
                <div key={item.category} className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div
                      className="w-4 h-4 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <div className="flex-1">
                      <span className="font-body font-semibold text-foreground block">{item.category}</span>
                      <span className="text-xs text-muted-foreground font-body">{item.description}</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="font-cta font-bold text-foreground">
                      ${(item.amount / 1000000).toFixed(1)}M
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {item.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
            <h3 className="text-2xl font-headline font-bold text-foreground mb-6 flex items-center space-x-2">
              <Icon name="ChartBarIcon" size={28} className="text-primary" variant="solid" />
              <span>Quarterly Performance</span>
            </h3>

            <div className="h-80 mb-6" aria-label="Quarterly Performance Bar Chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={quarterlyData}>
                  <XAxis dataKey="quarter" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => `$${(value / 1000000).toFixed(1)}M`} />
                  <Legend />
                  <Bar dataKey="raised" fill="#8B4513" name="Funds Raised" />
                  <Bar dataKey="deployed" fill="#D2691E" name="Funds Deployed" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-success/10 border border-success/30 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingUpIcon" size={20} className="text-success" variant="solid" />
                <span className="font-cta font-bold text-success">
                  83% Direct Impact Rate
                </span>
              </div>
              <p className="text-sm text-muted-foreground font-body">
                83 cents of every dollar goes directly to programs supporting vulnerable communities (Direct Aid, Education, Community Programs)
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {certifications.map((cert) => (
            <div
              key={cert.name}
              className="bg-card rounded-xl p-6 shadow-md border border-border text-center hover:scale-105 transition-transform duration-300"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={cert.icon as any} size={32} className="text-primary" variant="solid" />
              </div>
              <h4 className="font-cta font-bold text-foreground text-sm">
                {cert.name}
              </h4>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-xl p-8 shadow-lg border border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="DocumentTextIcon" size={40} className="text-primary" variant="solid" />
              </div>
              <h4 className="text-xl font-headline font-bold text-foreground mb-2">
                Annual Reports
              </h4>
              <p className="text-sm text-muted-foreground font-body mb-4">
                Comprehensive yearly financial statements and impact assessments
              </p>
              <button className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:bg-primary/90">
                Download Reports
              </button>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="ClipboardDocumentCheckIcon" size={40} className="text-primary" variant="solid" />
              </div>
              <h4 className="text-xl font-headline font-bold text-foreground mb-2">
                Independent Audits
              </h4>
              <p className="text-sm text-muted-foreground font-body mb-4">
                Third-party verification of all financial activities and fund usage
              </p>
              <button className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:bg-primary/90">
                View Audit Results
              </button>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="PresentationChartLineIcon" size={40} className="text-primary" variant="solid" />
              </div>
              <h4 className="text-xl font-headline font-bold text-foreground mb-2">
                Impact Metrics
              </h4>
              <p className="text-sm text-muted-foreground font-body mb-4">
                Real-time tracking of humanitarian outcomes and beneficiary reach
              </p>
              <button className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:bg-primary/90">
                Explore Metrics
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransparencySection;